import type { EnumObj } from "utils/typescript";
import {
  ISchema,
  mixed,
  MixedSchema,
  number,
  reach,
  Reference,
  SchemaDescription,
  SchemaFieldDescription,
  SchemaInnerTypeDescription,
  SchemaObjectDescription,
} from "yup";

// eslint-disable-next-line @typescript-eslint/ban-types
export type AnyPresentValue = {};

export const nullableNumber = number()
  .nullable()
  .transform((value: number | null, originalValue: unknown) =>
    originalValue === "" ? null : value,
  );

export function coerceNullableBoolean(value: unknown) {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "boolean") return value;

  const normalizedValue = String(value).trim().toLowerCase();
  if (["1", "true", "yes", "y"].includes(normalizedValue)) return true;
  if (["0", "false", "no", "n"].includes(normalizedValue)) return false;

  return value;
}

export function coerceByLabel<T extends string>(
  value: unknown,
  labels: Record<string, T>,
) {
  if (value === null || value === undefined) return null;

  const trimmedValue = String(value).trim();
  if (!trimmedValue) return null;

  return labels[trimmedValue] || trimmedValue;
}

function getEnumValues<E>(enumObj: EnumObj<E>) {
  return Object.values(enumObj as { [id: string]: string }) as E[];
}

export function getEnumSchema<E extends AnyPresentValue>(enumObj: EnumObj<E>) {
  return mixed<E>().oneOf(
    getEnumValues(enumObj),
    // eslint-disable-next-line no-template-curly-in-string
    "“${value}” is not one of the available options.",
  ) as MixedSchema<E>;
}

export function getNullableEnumSchema<E extends AnyPresentValue>(
  enumObj: EnumObj<E>,
) {
  return getEnumSchema<E>(enumObj).nullable();
}

export function getStringUnionSchema<U extends AnyPresentValue>(
  stringUnion: readonly U[],
) {
  return mixed<U>().oneOf(
    stringUnion.slice(0),
    // eslint-disable-next-line no-template-curly-in-string
    "“${value}” is not one of the available options.",
  ) as MixedSchema<U>;
}

export function isRef(ref: unknown): ref is Reference {
  if (!ref || typeof ref !== "object") return false;
  // eslint-disable-next-line no-underscore-dangle
  return "__isYupRef" in ref && (ref.__isYupRef as boolean);
}

export type ComparisonRef = {
  ref: Reference;
  getFromSchema: () => ISchema<unknown> | undefined;
};

export function getRefLabel(ref: ComparisonRef) {
  const fromSchema = ref.getFromSchema();
  if (!fromSchema) return;
  const field = reach(fromSchema, ref.ref.path);
  if (!field) return;
  const description = field.describe();
  if (!description || !("label" in description)) return;
  return description.label;
}

export type NotRefOrLazyDescription =
  | SchemaDescription
  | SchemaObjectDescription
  | SchemaInnerTypeDescription;

export function getIsNotRefOrLazyDescription(
  fieldDescription: SchemaFieldDescription,
): fieldDescription is NotRefOrLazyDescription {
  return "optional" in fieldDescription;
}
